/**
 * Money helpers.
 *
 * Rule (design spec §4.3): the WHMCS bridge always sends Toman as a decimal
 * string, the ledger and API work only in integer Rial (`bigint`), and the UI
 * shows Toman. 1 Toman = 10 Rial. Money is never a JavaScript `number`.
 */

import { toPersianDigits } from './digits';

/** Integer amount in Rial. */
export type Rial = bigint;

/** Basis points: 1% = 100, 100% = 10000. */
export const BP_DENOMINATOR = 10000n;

const TOMAN_RE = /^(-)?(\d+)(?:\.(\d{1,2}))?$/;

export interface TomanParseOptions {
  /**
   * What to do when the value has a non-zero second decimal (a fraction of a
   * Rial, e.g. "100.05" Toman = 1000.5 Rial).
   * - `'error'` (default): throw, because the value cannot be stored exactly.
   * - `'floor'`: drop the sub-Rial part (toward zero).
   */
  subRial?: 'error' | 'floor';
}

/**
 * Parse a WHMCS Toman decimal string ("3400000.00") into integer Rial (34000000n).
 *
 * Exact decimal parsing, no floating point. Accepts an optional leading "-",
 * digits, and up to 2 decimals. Surrounding whitespace is trimmed. Anything
 * else (exponents, commas, "+", ".5", "5.", 3+ decimals) throws a `RangeError`.
 */
export function tomanStringToRial(input: string, options: TomanParseOptions = {}): Rial {
  const m = TOMAN_RE.exec(input.trim());
  if (!m) throw new RangeError(`Invalid Toman amount: ${JSON.stringify(input)}`);
  const negative = m[1] === '-';
  const whole = m[2] as string;
  const frac = (m[3] ?? '').padEnd(2, '0');
  if (frac[1] !== '0' && (options.subRial ?? 'error') === 'error') {
    throw new RangeError(`Toman amount has a fraction of a Rial: ${JSON.stringify(input)}`);
  }
  const rial = BigInt(whole) * 10n + BigInt(frac[0] as string);
  return negative ? -rial : rial;
}

/**
 * Convert Rial to an exact Toman decimal string: 34000000n -> "3400000",
 * 34000005n -> "3400000.5", -15n -> "-1.5".
 */
export function rialToToman(rial: Rial): string {
  const negative = rial < 0n;
  const abs = negative ? -rial : rial;
  const whole = abs / 10n;
  const tenth = abs % 10n;
  const body = tenth === 0n ? whole.toString() : `${whole}.${tenth}`;
  return negative && abs !== 0n ? `-${body}` : body;
}

/**
 * Convert Rial to the Toman string format WHMCS stores (always 2 decimals),
 * e.g. for the bridge `addCredit` action: 34000005n -> "3400000.50".
 */
export function rialToWhmcsToman(rial: Rial): string {
  const negative = rial < 0n;
  const abs = negative ? -rial : rial;
  const body = `${abs / 10n}.${abs % 10n}0`;
  return negative ? `-${body}` : body;
}

/** Persian thousands separator (U+066C ARABIC THOUSANDS SEPARATOR). */
export const PERSIAN_THOUSANDS_SEPARATOR = '٬';
/** Persian decimal separator (U+066B ARABIC DECIMAL SEPARATOR). */
export const PERSIAN_DECIMAL_SEPARATOR = '٫';

export interface FormatTomanOptions {
  /** Use Persian digits and separators. Default `true`. */
  persianDigits?: boolean;
  /** Append " تومان". Default `true`. */
  withUnit?: boolean;
}

/**
 * Format Rial as a Toman string for display:
 * 34000000n -> "۳٬۴۰۰٬۰۰۰ تومان". With `persianDigits: false` -> "3,400,000 تومان".
 * A non-zero tenth of a Toman is shown as a decimal ("۱٫۵ تومان").
 */
export function formatToman(rial: Rial, options: FormatTomanOptions = {}): string {
  const persian = options.persianDigits ?? true;
  const withUnit = options.withUnit ?? true;
  const negative = rial < 0n;
  const abs = negative ? -rial : rial;
  const thousands = persian ? PERSIAN_THOUSANDS_SEPARATOR : ',';
  // Owner decision 2026-10-30: the smallest amount shown is one toman; a leftover rial is never shown.
  let out = (abs / 10n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  if (negative) out = `-${out}`;
  if (persian) out = toPersianDigits(out);
  return withUnit ? `${out} تومان` : out;
}

/** Whole toman (a multiple of 10 rial), rounded toward zero: the smallest unit money moves in. */
export function wholeToman(rial: bigint): bigint {
  return rial - (rial % 10n);
}

/**
 * `amount × bp / 10000` in integer Rial.
 *
 * For a non-negative amount this is the floor. `bigint` division truncates
 * toward zero, so for a negative amount the result is rounded toward zero
 * (e.g. bpOf(-15n, 1000) = -1n, not -2n). `bp` must be a non-negative integer.
 */
export function bpOf(amount: bigint, bp: number): bigint {
  assertBp(bp, 'bp');
  return (amount * BigInt(bp)) / BP_DENOMINATOR;
}

/** Throw unless `bp` is a non-negative safe integer (optionally bounded by `max`). */
export function assertBp(bp: number, name: string, max?: number): void {
  if (!Number.isSafeInteger(bp) || bp < 0) {
    throw new RangeError(`${name} must be a non-negative integer number of basis points, got ${bp}`);
  }
  if (max !== undefined && bp > max) {
    throw new RangeError(`${name} must be at most ${max} basis points, got ${bp}`);
  }
}
